// ===========================================
// GestionAdmins — Page liste des administrateurs (refonte UX)
// ===========================================
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Button, Tag, Grid, Popconfirm, Space, Tooltip, Switch, Empty,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined,
    KeyOutlined, CheckCircleOutlined, CrownOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { UserService } from '../../common/services';
import { AuthContext } from '../../common/constants';
import { getRoleLabel, getRoleColor } from '../../common/auth/roleLabels';
import { formatPhone } from '../../common/utils/format';

import MiniKpiCard from '../clients/components/MiniKpiCard';
import Can from '../../components/can';

import './gestion-admins.page.scss';

const getInitials = (f, l) => `${(f || '')[0] || ''}${(l || '')[0] || ''}`.toUpperCase();

const GestionAdmins = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const { currentUser } = useContext(AuthContext);

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const res = await UserService.getAdministrateurs();
            const raw = res?.data?.items || res?.data || res?.items || [];
            const data = Array.isArray(raw) ? raw : [];
            setAdmins(data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAdmins(); }, []);

    const filteredAdmins = useMemo(() => {
        if (!search) return admins;
        const q = search.toLowerCase();
        return admins.filter((a) =>
            `${a.firstName || ''} ${a.lastName || ''} ${a.phone || ''} ${a.email || ''} ${getRoleLabel(a.role) || ''}`
                .toLowerCase()
                .includes(q)
        );
    }, [admins, search]);

    // ─── KPIs ─────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const total = admins.length;
        const actifs = admins.filter((a) => a.isActive !== false).length;
        const superAdmins = admins.filter((a) => (a.role || '').toLowerCase().includes('super')).length;
        return { total, actifs, superAdmins };
    }, [admins]);

    const handleDelete = async (id) => {
        try {
            await UserService.deleteAdmin(id);
            toast.success('Administrateur supprimé');
            fetchAdmins();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleToggleActive = async (id, name) => {
        const previous = admins;
        setAdmins((prev) => prev.map((a) => (a._id === id ? { ...a, isActive: !a.isActive } : a)));
        try {
            const res = await UserService.toggleActive(id);
            const data = res?.data || res;
            toast.success(data?.message || `Statut de ${name} modifié`);
        } catch (error) {
            setAdmins(previous);
            toast.error(error?.message || 'Erreur lors du changement de statut');
        }
    };

    const handleResetPassword = async (id, name) => {
        try {
            await UserService.resetPassword(id);
            toast.success(`Mot de passe de ${name} réinitialisé. L'utilisateur devra le changer à sa prochaine connexion.`);
        } catch (error) {
            toast.error('Erreur lors de la réinitialisation');
        }
    };

    // ─── Colonnes ─────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Administrateur',
            key: 'user',
            width: 280,
            sorter: (a, b) =>
                `${a.firstName || ''}${a.lastName || ''}`.localeCompare(`${b.firstName || ''}${b.lastName || ''}`),
            render: (_, r) => (
                <div className="admin-cell">
                    <div className="admin-avatar">{getInitials(r.firstName, r.lastName)}</div>
                    <div className="admin-cell-body">
                        <span className="admin-name">
                            {`${r.firstName || ''} ${r.lastName || ''}`.trim() || '—'}
                        </span>
                        <span className="admin-phone">{r.phone ? formatPhone(r.phone, { fallback: '—' }) : '—'}</span>
                    </div>
                </div>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 240,
            ellipsis: true,
            render: (v) => v || <span className="admin-muted">—</span>,
        },
        {
            title: 'Rôle',
            dataIndex: 'role',
            key: 'role',
            width: 180,
            render: (r) => <Tag color={getRoleColor(r)} className="admin-role-tag">{getRoleLabel(r)}</Tag>,
        },
        {
            title: 'Statut',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 110,
            align: 'center',
            render: (v, record) => {
                const isSelf = currentUser && record._id === currentUser._id;
                return (
                    <Tooltip
                        title={isSelf
                            ? 'Vous ne pouvez pas modifier votre propre statut'
                            : (v !== false ? 'Cliquer pour désactiver' : 'Cliquer pour activer')}
                    >
                        <Switch
                            checked={v !== false}
                            checkedChildren="Actif"
                            unCheckedChildren="Inactif"
                            disabled={isSelf}
                            onChange={() => handleToggleActive(record._id, record.firstName)}
                        />
                    </Tooltip>
                );
            },
        },
        {
            title: 'Date création',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
            sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
            defaultSortOrder: 'descend',
            render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—',
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            align: 'center',
            fixed: isMobile ? false : 'right',
            render: (_, record) => {
                const isSelf = currentUser && record._id === currentUser._id;
                return (
                    <Space size={6}>
                        <Can perm="users:update">
                            <Tooltip title="Modifier">
                                <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    className="admin-action-btn admin-action-edit"
                                    aria-label="Modifier cet administrateur"
                                    onClick={() => navigate(`/dashboard/ajouter-admin?id=${record._id}`)}
                                />
                            </Tooltip>
                        </Can>
                        <Can perm="users:update">
                            {isSelf ? (
                                <Tooltip title="Vous ne pouvez pas réinitialiser votre propre mot de passe ici.">
                                    <Button
                                        size="small"
                                        icon={<KeyOutlined />}
                                        className="admin-action-btn"
                                        disabled
                                        aria-label="Réinitialiser mot de passe (désactivé)"
                                    />
                                </Tooltip>
                            ) : (
                                <Popconfirm
                                    title="Réinitialiser le mot de passe ?"
                                    description="Un nouveau mot de passe temporaire sera généré."
                                    okText="Oui"
                                    cancelText="Non"
                                    onConfirm={() => handleResetPassword(record._id, `${record.firstName} ${record.lastName}`)}
                                >
                                    <Tooltip title="Réinitialiser le mot de passe">
                                        <Button
                                            size="small"
                                            icon={<KeyOutlined />}
                                            className="admin-action-btn admin-action-reset"
                                            aria-label="Réinitialiser le mot de passe"
                                        />
                                    </Tooltip>
                                </Popconfirm>
                            )}
                        </Can>
                        <Can perm="users:delete">
                            {isSelf ? (
                                <Tooltip title="Vous ne pouvez pas supprimer votre propre compte">
                                    <Button
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        className="admin-action-btn"
                                        disabled
                                        aria-label="Supprimer (désactivé)"
                                    />
                                </Tooltip>
                            ) : (
                                <Popconfirm
                                    title="Supprimer cet administrateur ?"
                                    description="Cette action est irréversible."
                                    okText="Oui"
                                    cancelText="Non"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => handleDelete(record._id)}
                                >
                                    <Tooltip title="Supprimer">
                                        <Button
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            className="admin-action-btn admin-action-delete"
                                            aria-label="Supprimer cet administrateur"
                                        />
                                    </Tooltip>
                                </Popconfirm>
                            )}
                        </Can>
                    </Space>
                );
            },
        },
    ];

    return (
        <div className="gestion-admins-page">
            {/* Header */}
            <header className="admins-header">
                <div className="admins-header-text">
                    <h1 className="admins-title">
                        <TeamOutlined /> Administrateurs
                    </h1>
                    <p className="admins-subtitle">Gestion des comptes d'administration du back-office</p>
                </div>
                <div className="admins-header-actions">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/dashboard/ajouter-admin')}
                        className="admins-btn-add"
                    >
                        {!isMobile && 'Nouvel administrateur'}
                    </Button>
                </div>
            </header>

            {/* KPIs */}
            <section className="admins-kpis" aria-label="Indicateurs administrateurs">
                <MiniKpiCard
                    icon={<TeamOutlined />}
                    label="Total administrateurs"
                    value={stats.total}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<CheckCircleOutlined />}
                    label="Actifs"
                    value={stats.actifs}
                    tone="green"
                />
                <MiniKpiCard
                    icon={<CrownOutlined />}
                    label="Super administrateurs"
                    value={stats.superAdmins}
                    tone="orange"
                />
            </section>

            {/* Carte principale */}
            <Card className="admins-main-card">
                <div className="admins-toolbar">
                    <Input
                        placeholder="Rechercher un administrateur (nom, téléphone, email, rôle)…"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        className="admins-search"
                    />
                </div>

                <Table
                    dataSource={filteredAdmins}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucun administrateur" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    pagination={{
                        pageSize: 10,
                        showTotal: (t) => `${t} administrateur${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                    }}
                />
            </Card>
        </div>
    );
};

export default GestionAdmins;
